import { Col, Row, Typography } from "antd";
import AccountPhone from "../pages/OnboardingComponents/AccountPhone.jsx";
import AddPhone from "../pages/OnboardingComponents/AddPhone.jsx";
import AuthorizePage from "../pages/OnboardingComponents/AuthorizePage";
import GuidetoSendSms from "../pages/OnboardingComponents/GuideToSendSms.jsx";
import OnboardingFinishModalContent from "../pages/OnboardingComponents/OnboardingFinishModalContent.jsx";
import { getSmsSteps, getVoiceSteps, getWhatsAppSteps } from "../pages/OnboardingComponents/onboardingUtils/onboardingFinishModalSteps.jsx";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import { FB_API_VERSION, handleOnboardingRedirect } from "../pages/utils/commonUtils.js";
import { WHATSAPP_DISPLAY_NAME } from "./AppConstants.js";
import { MessageAreaInfo } from "../pages/utils/CommonVessels.js";

// Define FB_SDK_URL locally to avoid circular dependency issues
const FB_SDK_URL = "https://connect.facebook.net/en_US/sdk.js";

export const urlParams = new URLSearchParams(window.location.search);
const url = new URL(window.location.href);

const params = new URLSearchParams(url.search);

params.delete("sideBar");
params.delete("onBoardingPage");
params.delete("onInstall");

export const ProviderCommonObj = {
  isMMSSupported: false,
  isAlphaSenderSupported: false,
  category: "SMS",
  isMessagingServiceSenderSupported: false,
  serviceNotAuthorizedTitle: "How to authorize",
  serviceAuthorizationPageRightPanelTitle: null,
  serviceAuthorizationPageLeftPanelTitle: null,
  isEnableInboundSms: false,
  app: "",
  title: "",
  AuthorizationType: "",
  loginContent: [],
  steps: [
    {
      title: "account",
      description: "Account",
      content: <AccountPhone />,
    },
    {
      title: "installation",
      description: "Installation",
      content: <AuthorizePage />,
    },
    {
      title: "addPhone",
      description: "Add Phone",
      content: <AddPhone />,
    },
    {
      title: "guide",
      description: "Guide to send SMS",
      content: <GuidetoSendSms />,
    },
  ],
  menu: [],
  supportedAttachmentTypes: {
    image: "image/png, image/jpeg, image/jpg, image/gif",
    audio: "audio/mpeg",
    video: "video/mp4, video/mpeg",
    file: "application/pdf",
  },
  maximumAttachmentSizeInBytes: 5242880, // 5MB
  maximumAttachmentLimit: 5,
  isShowAllFeatureNotSupported: false,
  handleSavedNumberProcessingForOnboardingPageRedirect: (appProperties, integId, location, navigate, searchParams, savedNumbers) => {
    const hasSavedNumbers = appProperties?.savedNumbers?.some((obj) => obj?.integId === integId);
    handleOnboardingRedirect(location, navigate, searchParams, hasSavedNumbers);
  },
  disconnectServiceConfirmationMessage: "Disconnecting this service will stop your clients from receiving updates via campaigns, automations, or 1-1 messaging, and you will miss incoming messages. Are you sure you want to proceed with disconnecting the service?",
};

export const ProviderExtendedCommonObj = {
  hello_send: {
    howToAuthLink: null,
    app: "hello_send",
    category: "sms",
    isEnableInboundSms: false,
    supportedAttachmentTypes: {
      image: "image/png, image/jpeg, image/jpg, image/gif",
    },
    maximumAttachmentSizeInBytes: 5242880, // 5MB
    maximumAttachmentLimit: 5,
    isMessagingServiceSenderSupported: false,
    onBoardingConnected: true,
  },
  twilio: {
    AuthorizationType: "auth",
    serviceNotAuthorizedTitle: "How to Authorize Twilio",
    AuthorizeReference: [
      {
        description: (
          <>
            Go to{" "}
            <a href="https://www.twilio.com/" target="_blank" rel="noopener noreferrer">
              https://www.twilio.com/
            </a>{" "}
            and click on the 'Sign up' button to create a new account. If you already have an account, log in to your account.
          </>
        ),
      },
      {
        description: <>On the dashboard, you will see your Account SID and Auth token.</>,
      },
      {
        description: <>Copy your Account SID and Auth token and use them to authenticate Twilio service.</>,
      },
    ],
    serviceAuthorizationPageRightPanelTitle: "How to Authorize",
    serviceAuthorizationPageLeftPanelTitle: "Authorize Twilio",
    AuthorizeReferenceText: "That's it! You now have your Twilio Account SID and Auth token.",
    AuthorizeReferenceURL: "https://www.youtube.com/embed/hv-P_qDN2-Y",
    app: "twilio",
    category: "sms",
    howToAuthLink: "https://www.hellosend.com/help/twilio-sms-mms-integration",
    title: "Authorize Twilio to connect SMS numbers.",
    supportedAttachmentTypes: {
      image: "image/png, image/jpeg, image/jpg, image/gif",
      video:
        "video/mpeg,video/mp4,video/quicktime,video/webm,video/3gpp,video/3gpp2,video/3gpp-tt,video/H261,video/H263,video/H263-1998,video/H263-2000,video/H264",

      file: "application/pdf, application/vcard, application/vnd.apple.pkpass, text/vcard, text/x-vcard, text/csv, text/rtf, text/richtext, text/calendar, text/directory",
    },
    maximumAttachmentSizeInBytes: 5242880, // 5MB
    maximumAttachmentLimit: 5,
    isAlphaSenderSupported: true,
    isEnableInboundSms: false,
    isMessagingServiceSenderSupported: true,
    menu: [
      {
        label: "Add Phone",
        key: "Phone number",
      },
      {
        label: "Messaging Service",
        key: "MessagingService",
      },
      {
        label: "Alpha Sender",
        key: "AlphaSender",
      },
    ],
    onBoardingFlow: {
      getFinishModalComponent: (appProperties) => {
        return {
          steps: getSmsSteps(),
          head: "SMS capability successfully activated!",
          subHead: "Ready to elevate your customer communication? Here’s how:",
          videoGallery: appProperties?.controller?.SMS_GALLERY,
        };
      },
    },
  },
  twilio_voice: {
    AuthorizationType: "auth",
    AuthorizeReference: [
      {
        description: (
          <>
            Go to{" "}
            <a href="https://www.twilio.com/" target="_blank" rel="noopener noreferrer">
              https://www.twilio.com/
            </a>{" "}
            and click on the 'Sign up' button to create a new account. If you already have an account, log in to your account.
          </>
        ),
      },
      {
        description: <>On the dashboard, you will see your Account SID and Auth token.</>,
      },
      {
        description: <>Copy your Account SID and Auth token and use them to authenticate Twilio service.</>,
      },
    ],
    AuthorizeReferenceText: "That's it! You now have your Twilio Account SID and Auth token.",
    AuthorizeReferenceURL: "https://www.youtube.com/embed/hv-P_qDN2-Y",
    app: "twilio",
    category: "call",
    howToAuthLink: "https://www.hellosend.com/help/twilio-sms-mms-integration",
    title: "Authorize Twilio to connect call numbers.",
    supportedAttachmentTypes: {
      image: "image/png, image/jpeg, image/jpg, image/gif",
      file: "application/pdf, application/vcard, application/vnd.apple.pkpass, text/vcard, text/x-vcard, text/csv, text/rtf, text/richtext, text/calendar, text/directory",
    },
    maximumAttachmentSizeInBytes: 5242880, // 5MB
    maximumAttachmentLimit: 5,
    isAlphaSenderSupported: true,
    isEnableInboundSms: false,
    isMessagingServiceSenderSupported: true,
    menu: [
      {
        label: "Add Phone",
        key: "Phone number",
      },
    ],
    onBoardingFlow: {
      getFinishModalComponent: (appProperties, callProperties, setCallProperties) => {
        const callFlowEndPoint = `/voice/callFlow/edit/${callProperties?.savedNumbers?.[0]?.callFlowId}/phoneNumber/${encodeURIComponent(
          callProperties?.savedNumbers?.[0]?.phoneNumber
        )}`;
        const handleMakeTestCall = () => {
          if (appProperties) {
            window.open(appProperties?.domain, "_blank");
          }
        };
        return {
          steps: getVoiceSteps(callFlowEndPoint, appProperties),
          note: "Set Up Call Teams and Call Flow can be verified and adjusted later.",
          head: "Call capability successfully activated!",
          subHead: (
            <div className="d-flex flex-row">
              <div>Ready to make your first call?</div>
              <div className="ps-1 hs-fs-12 hs-fw-500 hs-color-violet cursor-pointer" onClick={handleMakeTestCall}>
                Make Test Call
              </div>
            </div>
          ),
          videoGallery: appProperties?.controller?.VOICE_GALLERY,
        };
      },
    },
    handleSavedNumberProcessingForOnboardingPageRedirect: (appProperties, integId, location, navigate, searchParams, savedNumbers) => {
      const hasSavedNumbers = savedNumbers?.length > 0;
      handleOnboardingRedirect(location, navigate, searchParams, hasSavedNumbers);
    },
    disconnectServiceConfirmationMessage: "Disconnecting this service will stop inbound and outbound calls, and you may miss calls from your customers. Are you sure you want to proceed with disconnecting the service?",
  },

  facebook: {
    howToAuthLink: "https://help.oapps.xyz/portal/en/kb/articles/how-to-authorise#How_to_Authorise_Facebook",
    category: "messenger",
    AuthorizationType: "outh",
    supportedAttachmentTypes: {
      image: "image/png, image/jpeg, image/jpg, image/gif",
    },
    maximumAttachmentLimit: 5,
    maximumAttachmentSizeInBytes: 5242880, // 5MB
    isEnableInboundSms: false,
    app: "facebook",
    title: "Connect facebook and configure all pages",
    menu: [
      {
        label: "Select Pages",
        key: "Select Pages",
      },
    ],
    onBoardingFlow: {
      getFinishModalComponent: (appProperties) => {
        return {
          steps: getSmsSteps(),
          head: "SMS capability successfully activated!",
          subHead: "Ready to elevate your customer communication? Here’s how:",
        };
      },
    },
  },
  ringcentral: {
    howToAuthLink: "https://www.hellosend.com/help/ringcentral-sms-mms-integration",
    AuthorizationType: "oauth",
    serviceNotAuthorizedTitle: "How to Authorize Ringcentral",
    serviceAuthorizationPageRightPanelTitle: "How to Authorize",
    serviceAuthorizationPageLeftPanelTitle: "Authorize Ringcentral",
    AuthorizeReference: [
      {
        description: "Begin by clicking on the 'Authorize' button.",
      },
      {
        description: "Enter your RingCentral username and password.",
      },
      {
        description: "Click on the 'Authorize' button to grant permission.",
      },
      {
        description: "Once completed, your RingCentral account is connected.",
      },
      {
        description: "In the right panel, you'll find all SMS-enabled numbers available for configurations.",
      },
    ],
    AuthorizeReferenceURL: "https://www.youtube.com/embed/U_HI1fQe8MM?si=oGTp9XcgM-lCXQY0",
    category: "sms",
    isEnableInboundSms: true,
    app: "ringcentral",
    // title: "Authorize Ringcentral to connect SMS numbers.",
    descriptionTitle: "Connect your RingCentral account to HelloSend to send and receive SMS instantly.",
    description:
      "\nManage all conversations in one place, and automate messages with workflows.Enjoy centralized communication, automated follow-ups, secure delivery, and reliable performance — all through a single, unified platform.",
    supportedAttachmentTypes: {
      image: "image/png, image/jpeg, image/jpg, image/gif",
      audio: "audio/mpeg",
      video: "video/3gpp, video/mp4, video/mpeg, video/msvideo",
      file: "application/rtf, text/vcard, application/zip, application/gzip",
    },
    maximumAttachmentLimit: 5,
    maximumAttachmentSizeInBytes: 1572864, // 1.5MB
    onBoardingFlow: {
      getFinishModalComponent: (appProperties) => {
        return {
          steps: getSmsSteps(),
          head: "SMS capability successfully activated!",
          subHead: "Ready to elevate your customer communication? Here’s how:",
          videoGallery: appProperties?.controller?.SMS_GALLERY,
        };
      },
    },
  },
  teams_helloSend: {
    howToAuthLink: "https://www.hellosend.com/help/sms-whatsapp-integration-microsoft-teams",
    AuthorizationType: "no_auth",
    category: "sms",
    isEnableInboundSms: false,
    app: "teams_hellosend",
    title: "Authorize Teams and port all your numbers.",
    menu: [
      {
        label: "Add Phone",
        key: "Phone number",
      },
    ],
    supportedAttachmentTypes: {
      image: "image/png, image/jpeg, image/jpg, image/gif",
      file: "application/pdf, application/vcard, application/vnd.apple.pkpass, text/vcard, text/x-vcard, text/csv, text/rtf, text/richtext, text/calendar, text/directory",
    },
    maximumAttachmentLimit: 5,
    maximumAttachmentSizeInBytes: 5242880, // 5MB
    isMMSSupported: true,
    AuthorizeReference: [
      {
        description: "Begin by clicking the 'Authorize' button.",
      },
      {
        description: "Enter your Microsoft Teams username and password.",
      },
      {
        description: "Verify your account by following the prompts.",
      },
      {
        description: "Once your account is verified, all your phone numbers will be listed for configuration.",
      },
    ],
    AuthorizeReferenceURL: "https://www.youtube.com/embed/CWnXcFlWm4A",
  },
  whatsapp: {
    howToAuthLink: "https://help.oapps.xyz/portal/en/kb/articles/how-to-authorise#How_to_Authorise_Facebook",
    category: "whatsapp",
    AuthorizationType: "oauth_sdk",
    serviceNotAuthorizedTitle: "WABA Features",
    AuthorizeReferenceURL: "https://www.youtube.com/embed/FgJ_OhCa0UA",
    serviceAuthorizationPageRightPanelTitle: "WABA Features",
    serviceAuthorizationPageLeftPanelTitle: "Authorize WABA",
    serviceAuthorizationButtonName: "Connect via Facebook",
    isShowAllFeatureNotSupported: true,
    AuthorizeReference: [
      {
        title: "Connection",
        description: <>Established via a Facebook account.</>,
      },
      {
        title: "Outbound Messages",
        description: <>Can only initiate contact using WhatsApp pre-approved templates.</>,
      },
      {
        title: "24-Hour Messaging Window",
        description: (
          <>Messages must be sent within 24 hours of the last customer interaction; after that, a pre-approved template is required.</>
        ),
      },
      {
        title: "Media Attachments",
        description: <>Supports sending images, documents, and other media.</>,
      },
      {
        title: "Messaging Options",
        description: <>Supports Direct, Campaign, and Automated messaging.</>,
      },
      {
        title: "Multiple Numbers",
        description: <>Allows multiple numbers per account.</>,
      },
    ],
    supportedAttachmentTypes: {
      image: "image/png, image/jpeg, image/jpg",
      audio: "audio/aac, audio/amr, audio/mpeg, audio/mp4, audio/ogg",
      video: "video/3gp, video/mp4",
      file: "text/plain, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/pdf",
    },
    maximumAttachmentLimit: 5,
    maximumAttachmentSizeInBytes: 20971520, // 20MB
    onAuthorizeOpenSuccessModal: true,
    isMMSSupported: true,
    isEnableInboundSms: false,
    baseUrlForTheScript: FB_SDK_URL,
    loadScripts: () => {
      return new Promise((resolve) => {
        try {
          window.fbAsyncInit = function () {
            window.FB.init({
              appId: process.env.REACT_APP_FB_APP_ID,
              autoLogAppEvents: true,
              xfbml: true,
              version: FB_API_VERSION,
            });
          };
          (function (d, s, id) {
            var js,
              fjs = d.getElementsByTagName(s)[0];
            if (d.getElementById(id)) return;
            js = d.createElement(s);
            js.id = id;
            js.src = FB_SDK_URL;
            fjs.parentNode.insertBefore(js, fjs);
          })(document, "script", "facebook-jssdk");
        } catch (error) {
        } finally {
          resolve(FB_SDK_URL);
        }
      });
    },
    doUnloadAuthorizationProcess: () => {},
    getHandleMessageAuthEvent: (wabaId) => {
      const handleAuthMessageEvent = (event) => {
        if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === "WA_EMBEDDED_SIGNUP" && data.event === "FINISH") {
            wabaId.current = data.data.waba_id;
          }
        } catch {}
      };
      return handleAuthMessageEvent;
    },
    doUnloadAuthorizationProcess: (listenerFunctionToRemove) => {
      window.removeEventListener("message", listenerFunctionToRemove);
    },
    getAuthorizeFunction: (callBackFunction, handleAuthMessageEvent) => {
      return new Promise((resolve) => {
        if (handleAuthMessageEvent) {
          window.addEventListener("message", handleAuthMessageEvent);
        }

        // Launch method and callback registration
        const launchWhatsAppSignup = () => {
          window.FB.login(callBackFunction, {
            config_id: process.env.REACT_APP_FB_CONFIG_ID,
            response_type: "code",
            override_default_response_type: true,
            extras: {
              setup: {},
              featureType: "",
              sessionInfoVersion: "3",
            },
          });
        };
        resolve(launchWhatsAppSignup);
      });
    },
    app: "whatsapp",
    title: `Connect your ${WHATSAPP_DISPLAY_NAME} Business account via Facebook for a quick setup and enhanced customer engagement.`,
    description: <></>,
    note: (
      <MessageAreaInfo
        animated={false}
        id="usage_limit_info"
        type="warning"
        message={
          <div>
            <ExclamationCircleOutlined className="pe-2" style={{ fontSize: "1rem", color: "#FF9800" }} />
            Marketing templates will not be delivered to U.S. (+1) WhatsApp numbers. Any messages sent will return an error.
          </div>
        }
      />
    ),
    helper: [
      <>
        Click <b>'Connect'</b> to log into your Facebook Business account.
      </>,
      <>Follow the steps to link or register your {WHATSAPP_DISPLAY_NAME} number and back up your data if using a personal number.</>,
    ],
    successAuthModal: {
      title: "Congratulations !",
      content: `Your account has been successfully connected your ${WHATSAPP_DISPLAY_NAME} account. Please add Templates to continue.`,
      button: {
        ok: {
          text: "Add Templates",
        },
        cancel: {
          text: "Cancel",
        },
      },
    },
    onBoardingFlow: {
      getFinishModalComponent: (appProperties) => {
        return {
          render: <OnboardingFinishModalContent serviceName="whatsapp" />,
          steps: getWhatsAppSteps(WHATSAPP_DISPLAY_NAME, appProperties),
          calculateStep: async (fetchSavedTemplates) => {
            return new Promise((resolve, reject) => {
              var step = 0;
              const whatsappProp = appProperties.installedApps.find((installedApp) => installedApp?.right?.service_name === "whatsapp");
              if (whatsappProp?.right?.auth?.authorized) {
                step = step + 2;
              }

              fetchSavedTemplates?.(appProperties)
                .then((res) => {
                  if (res?.success) {
                    const hasWhatsappTemplate = res.data?.some((template) => template.integId === whatsappProp?.integProps?.integId);
                    if (hasWhatsappTemplate) {
                      step = step + 1;
                    }
                  }
                })
                .finally(() => {
                  resolve(step);
                });
            });
          },
          getGallery: (appName) => {
            return appProperties?.controller?.WHATSAPP_GALLERY;
          },
        };
      },
    },
  },
  unipile_whatsapp: {
    title: `Connect ${WHATSAPP_DISPLAY_NAME} and configure all numbers`,
    description: <>Effortlessly connect and start messaging</>,
    howToAuthLink: "https://help.oapps.xyz/portal/en/kb/articles/how-to-authorise#How_to_Authorise_Facebook",
    serviceAuthorizationPageRightPanelTitle: "Whatsapp Features",
    serviceAuthorizationPageLeftPanelTitle: "Authorize Whatsapp",
    serviceAuthorizationButtonName: "Connect via QR Code",
    category: "whatsapp",
    AuthorizeReference: [
      {
        title: "Messaging Options",
        description: <>Supports Direct and Automated messaging.</>,
      },
      {
        title: "Message History",
        description: <>Old message history will not be deleted after connection.</>,
      },
      {
        title: "Automatic Sync",
        description: <>Messages sync instantly across linked devices.</>,
      },
      {
        title: "Better Engagement",
        description: <>Higher open and response rates compared to emails.</>,
      },
      {
        title: "Media Attachments",
        description: <>Supports sending images, videos, documents, and other media.</>,
      },
      {
        title: "Versatile Use Cases",
        description: <>Ideal for customer support, promotions, and automated updates.</>,
      },
      {
        title: "Number Limitation",
        description: <>Only one number per account.</>,
      },
    ],
    AuthorizationType: "unipile_auth",
    supportedAttachmentTypes: {
      image: "image/png, image/jpeg, image/jpg",
      audio: "audio/aac, audio/amr, audio/mpeg, audio/mp4, audio/ogg",
      video: "video/3gp, video/mp4",
      file: "text/plain, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/pdf",
    },
    maximumAttachmentLimit: 5,
    maximumAttachmentSizeInBytes: 20971520, // 20MB
    onAuthorizeOpenSuccessModal: true,
    isMMSSupported: true,
    isEnableInboundSms: false,
    app: "unipile_whatsapp",
    note: (
      <div className="d-flex flex-column gap-2 mt-3 hs-w-90">
        <Row gutter={[0, 16]} className="hs-bg-light-grey-3 hs-border-12 p-3">
          <Row className="w-100">
            <Typography.Text strong>Supported Mobile App Types</Typography.Text>
          </Row>
          <Row gutter={[0, 12]}>
            <Col span={24}>
              <Row>
                <Col>
                  <div className="actionIconsSprite single-blue-tick-icon" />
                </Col>
                <Col className="d-flex gap-1">
                  <div className="numberlist-sprites numberlist-unipile_whatsapp" />
                  <div className="my-auto">Whatsapp</div>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row>
                <Col>
                  <div className="actionIconsSprite single-blue-tick-icon" />
                </Col>
                <Col className="d-flex gap-1">
                  <div className="numberlist-sprites numberlist-whatsapp" />
                  <div className="my-auto">Whatsapp Business</div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Row>
        <MessageAreaInfo
          id="usage_limit_info"
          type="warning"
          animated={false}
          message={
            <div>
              <ExclamationCircleOutlined className="pe-2" style={{ fontSize: "1rem", color: "#FF9800" }} />
              <b>Note</b>: WhatsApp will automatically disconnect if the mobile app stays offline for 14 days. Please keep the app online to ensure
              uninterrupted service.
            </div>
          }
        />
      </div>
    ),
    AuthorizeReferenceURL: "https://www.youtube.com/embed/xwMyGrTZbbo",
    getAuthorizeFunction: () => {},
    helper: [],
    // title: <>Effortlessly connect and start messaging</>,
    onBoardingFlow: {
      getFinishModalComponent: (appProperties) => {
        return {
          render: <OnboardingFinishModalContent serviceName="whatsapp" />,
          steps: getWhatsAppSteps("unipile_whatsapp", appProperties),
          head: "WhatsApp capability has been successfully activated.",
          subHead: "Ready to start messaging? 👉 Import Your WhatsApp Contacts",
          videoGallery: appProperties?.controller?.UNIPILE_WHATSAPP_GALLERY,
          hideGoToButton: true,
        };
      },
    },
  },
  zoom: {
    howToAuthLink: "https://www.hellosend.com/help/hellosend-sms-for-zoom-phone",
    AuthorizationType: "oauth",
    serviceNotAuthorizedTitle: "How to Authorize Zoom",
    serviceAuthorizationPageRightPanelTitle: "How to Authorize",
    serviceAuthorizationPageLeftPanelTitle: "Authorize Zoom",
    AuthorizeReference: [
      {
        description: "Begin by clicking on the 'Authorize' button.",
      },
      {
        description: "Enter your Zoom username and password.",
      },
      {
        description: "Click on the 'Authorize' button to grant permission.",
      },
      {
        description: "Once completed, your Zoom account is connected.",
      },
      {
        description: "In the right panel, you'll find all SMS-enabled numbers available for configurations.",
      },
    ],
    AuthorizeReferenceURL: "https://www.youtube.com/embed/NUOTqZhWRUo",
    category: "sms",
    isEnableInboundSms: true,
    app: "zoom",
    title: "Authorize Zoom to connect SMS numbers.",
    supportedAttachmentTypes: {
      image: "image/png, image/jpeg, image/jpg, image/gif",
    },
    maximumAttachmentLimit: 5,
    maximumAttachmentSizeInBytes: 2097152, // 2MB
    onBoardingFlow: {
      getFinishModalComponent: (appProperties) => {
        return {
          steps: getSmsSteps(),
          head: "SMS capability successfully activated!",
          subHead: "Ready to elevate your customer communication? Here’s how:",
          videoGallery: appProperties?.controller?.SMS_GALLERY,
        };
      },
    },
  },
};
